import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import AppLayout from "./components/app-layout";
import Login from "./pages/Login";
import Inventory from "./pages/Inventory";
import Sites from "./pages/Sites";
import Quotes from "./pages/Quotes";
import Invoices from "./pages/Invoices";
import Reporting from "./pages/Reporting";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import "./styles/fonts.css";
import Activities from "./pages/Activities";
import AddActivity from "./pages/AddActivity";
import Warehouses from "./pages/Warehouses";
import Customers from "./pages/Customers";
import Vendors from "./pages/Vendors";
import SiteDetails from "./pages/SiteDetails";
import CreateQuotation from "./pages/CreateQuotation";
import CreateInvoice from "./pages/CreateInvoice";
import CustomerDetails from "./pages/CustomerDetails";
import PpePage from "./pages/Ppe";
import MobileApp from "./pages/mobile-app/MobileApp";
import Reminders from "./pages/Reminders";
import FixedAssets from "./pages/FixedAssets";
import Settings from "./pages/Settings";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";

// Define types for ErrorBoundary
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

// Error Boundary Component
class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center p-8 bg-white rounded-lg shadow-lg">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-4">{this.state.error?.message}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function AppContent() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/activities" element={<Activities />} />
          <Route path="/add-activity" element={<AddActivity />} />
          <Route path="/warehouses" element={<Warehouses />} />
          <Route path="/inventory" element={<Inventory />} />
          <Route path="/ppe" element={<PpePage />} />
          <Route path="/mobile-app" element={<MobileApp />} />
          <Route path="/shop" element={<Navigate to="/mobile-app" replace />} />
          <Route path="/site-services" element={<Navigate to="/mobile-app" replace />} />
          <Route path="/mobile-app/shop" element={<Navigate to="/mobile-app" replace />} />
          <Route path="/mobile-app/services" element={<Navigate to="/mobile-app" replace />} />
          <Route path="/reminders" element={<Reminders />} />
          <Route path="/assets" element={<FixedAssets />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/vendors" element={<Vendors />} />
          <Route path="/sites" element={<Sites />} />
          <Route path="/site-details" element={<SiteDetails />} />
          <Route path="/customer-details" element={<CustomerDetails />} />
          <Route path="/quotes" element={<Quotes />} />
          <Route path="/create-quotation" element={<CreateQuotation />} />
          <Route path="/create-invoice" element={<CreateInvoice />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/users" element={<Users />} />
          <Route path="/reporting" element={<Reporting />} />
          <Route element={<AdminRoute />}>
            <Route path="/settings" element={<Settings />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AppContent />
        <Toaster position="top-center" />
      </Router>
    </ErrorBoundary>
  );
}

export default App;
